import { type ModalOptions, modalController } from '@ionic/vue'

import type { OnboardingQueries } from '~/types/onboard'
import ModalResetPassword from '~/views/login/ModalResetPassword.vue'
import ModalSignIn from '~/views/login/ModalSignIn.vue'

const resetPasswordModalId = 'password-reset-modal'
const signInModalId = 'sign-in-modal'

interface ShowSignInModalOptions {
  queries?: OnboardingQueries
  hideBackButton?: boolean
  hideSignUpLink?: boolean
  onSuccessfulSignIn?: () => Promise<void>
}

/**
 * @description Handler to display a modal for the user to sign in.
 */
export function useSignInModal() {
  const modalOptions: ModalOptions = {
    breakpoints: [0, 1],
    component: ModalSignIn,
    id: signInModalId
  }

  /**
   * @description Display the sign in modal
   * @param queries {OnboardingQueries} The queries to pass during onboarding and order-at-table flows
   * @param hideBackButton {boolean} Whether to hide the back header button
   * @param hideSignUpLink {boolean} Whether to hide the sign-up link
   * @param onSuccessfulSignIn {() => Promise<void>} Callback to run after a successful sign in
   */
  async function showModal({ queries, hideSignUpLink, hideBackButton, onSuccessfulSignIn }: ShowSignInModalOptions) {
    const modal = await modalController.create({
      ...modalOptions,
      componentProps: {
        modalId: signInModalId,
        queries,
        hideBackButton,
        hideSignUpLink,
        onSuccessfulSignIn
      }
    })

    if (!modal) {
      return
    }

    return await modal.present()
  }

  return {
    showSignInModal: showModal,
    signInModalId
  }
}

export function useResetPasswordModal() {
  const modalOptions: ModalOptions = {
    breakpoints: [0, 1],
    component: ModalResetPassword,
    id: resetPasswordModalId
  }

  /**
   * @description Display the password reset modal
   * @param queries {OnboardingQueries} The queries to pass during onboarding and order-at-table flows
   */
  async function showModal(queries?: OnboardingQueries) {
    const modal = await modalController.create({
      ...modalOptions,
      componentProps: {
        modalId: resetPasswordModalId,
        ...queries
      }
    })

    if (!modal) {
      return
    }

    return await modal.present()
  }

  return {
    showResetPasswordModal: showModal,
    resetPasswordModalId
  }
}
